// Note Expander v2, the "Your style" page's pure logic (PR 8a-UI; DESIGN §8.1, mockup screen 6),
// driven directly by tests/unit/v3/note-expander-v2-style-lib.test.js.
//
//   The server decides what was learned and what is in use (GET /v2/me/style: `approved.hasLearned`
//   is the test the runs use). What lives here is presentation: the section words, the range presets
//   and their checks, the post-expansion warning, which profile the page shows, the header chip, the
//   "learned from" line, and the inbox banner's dismissal. Dates stay 'YYYY-MM-DD' strings: never
//   new Date() (formatIsoDate reads the string itself).

const { formatIsoDate } = require('./note-expander-v2');

// Elation's visit-note bullet categories (the `category` enum of its visit-note API) in words.
const SECTION_LABELS = Object.freeze({
  Hpi: 'HPI', Reason: 'Reason for visit', Problem: 'Problems', ROS: 'Review of Systems', PE: 'Physical Examination',
  Objective: 'Objective', Data: 'Data', Test: 'Tests', Assessment: 'Assessment', Plan: 'Plan', Assessplan: 'Assessment and Plan',
  Tx: 'Treatment', Instr: 'Instructions', Followup: 'Follow-up', Narrative: 'Narrative', Past: 'Past history',
  Family: 'Family history', Social: 'Social history', Habits: 'Habits', Med: 'Medications', Allergies: 'Allergies',
  Surgical: 'Surgical history', Procedure: 'Procedure', Dateprocedure: 'Procedure date', Orders: 'Orders',
  Consultant: 'Consultant', Attending: 'Attending',
});

function sectionLabel(category) { return SECTION_LABELS[category] || String(category); }

// The physician's usual sections, in order: those in at least half of the notes it learned from.
function sectionOrder(structure) {
  const sections = structure && Array.isArray(structure.sections) ? structure.sections : [];
  return sections.filter((s) => s && s.share >= 0.5).map((s) => sectionLabel(s.category));
}

// The habits a profile learned (ne-style-1 v3), as the page lists them: each with its id (what Remove
// names), its text, how many notes showed it and one de-identified quote. → [{ id, text, notes, example }]
function habitsOf(profile) {
  const list = profile && profile.structure && Array.isArray(profile.structure.habits) ? profile.structure.habits : [];
  return list.filter((h) => h && typeof h.id === 'string' && typeof h.text === 'string' && h.text.trim())
    .map((h) => ({ id: h.id, text: h.text, notes: Number.isInteger(h.notes) ? h.notes : null, example: typeof h.example === 'string' ? h.example : null }));
}

// A learned phrase's body system: the pack's own word, which the view sends (systemLabel); the
// system's id, capitalised, otherwise.
function systemLabel(phrase) {
  if (phrase && phrase.systemLabel) return phrase.systemLabel;
  const s = String((phrase && phrase.system) || '');
  return s ? s[0].toUpperCase() + s.slice(1) : '';
}

// The live count in words. count: { state: 'idle' | 'loading' | 'ok' | 'unlinked' | 'error', n }
function countWords(count) {
  if (!count) return '';
  if (count.state === 'loading') return 'Counting your notes…';
  if (count.state === 'error') return 'The count is not available right now.';
  if (count.state !== 'ok') return '';
  if (count.n == null) return 'Elation did not say how many notes are in this range.';
  return `${Number(count.n).toLocaleString('en-US')} signed ${count.n === 1 ? 'note' : 'notes'} in this range`;
}

const PRESETS = Object.freeze(['m6', 'm12', 'all', 'custom']);

function presetLabel(key, firstExpansion) {
  if (key === 'm6') return firstExpansion ? '6 months before the expander' : 'The last 6 months';
  if (key === 'm12') return firstExpansion ? '12 months before the expander' : 'The last 12 months';
  if (key === 'all') return 'All my notes';
  return 'Custom';
}

// → { from, to } of a preset, from the server's own date maths (the view's `presets`)
function presetRange(view, key) {
  const p = view && view.presets && view.presets[key];
  return p ? { from: p.from, to: p.to } : null;
}

const YMD = /^\d{4}-\d{2}-\d{2}$/;

// The build's own checks, in the page's words. count: Elation's (null while unknown).
// → '' when the range may be built, else why not
function rangeProblem(range, today, count, minNotes) {
  if (!range || !YMD.test(range.from || '') || !YMD.test(range.to || '')) return 'Choose a start date and an end date.';
  if (range.from > range.to) return 'The start date must be on or before the end date.';
  if (today && range.to > today) return 'The end date cannot be in the future.';
  if (count != null && count < minNotes) return `Only ${count} of your signed notes are in this range. At least ${minNotes} are needed.`;
  return '';
}

// The range reaches notes signed on or after the practice's first expansion.
function crossesExpansion(range, firstExpansion) {
  return !!(firstExpansion && range && YMD.test(range.to || '') && range.to >= firstExpansion);
}

function expansionWarning(firstExpansion) {
  return `Notes signed on or after ${formatIsoDate(firstExpansion)}, when the Note Expander started at your practice, may contain its text. `
    + 'Notes it wrote or expanded are skipped automatically, so fewer may be used than shown.';
}

// What "What we learned" shows: the learned draft waiting for review, else the approved learned
// style, else nothing. → { profile, kind: 'draft' | 'approved' } or null
function shownProfile(view) {
  if (!view) return null;
  if (view.draft && view.draft.state === 'learned') return { profile: view.draft, kind: 'draft' };
  if (view.approved && view.approved.hasLearned) return { profile: view.approved, kind: 'approved' };
  return null;
}

// The header chip. → { label, tone: 'ok' | 'warn' | '' }
function styleChip(view) {
  if (view && view.draft && view.draft.state === 'learned') return { label: 'Ready to review', tone: 'warn' };
  if (view && view.draft && view.draft.state === 'building') return { label: 'Building', tone: '' };
  if (view && view.approved && view.approved.hasLearned && String(view.approved.changeReason || '').startsWith('updated:')) {
    const day = approvedDate(view.approved.approvedAt);
    return { label: `Updated${day ? ` ${day}` : ''} · version ${view.approved.version}`, tone: 'ok' };
  }
  if (view && view.approved && view.approved.hasLearned) return { label: `In use · version ${view.approved.version}`, tone: 'ok' };
  return { label: 'Standard style', tone: '' };
}

// 'Jan 3 – Jul 2, 2026', or 'Dec 3, 2025 – Jul 2, 2026' across years.
function dateSpan(first, last) {
  const a = formatIsoDate(first);
  const b = formatIsoDate(last);
  if (!a || !b) return a || b || '';
  if (first.slice(0, 4) === last.slice(0, 4)) return `${a.replace(/, \d{4}$/, '')} – ${b}`;
  return `${a} – ${b}`;
}

// "Version 3 · learned from 38 notes signed Jan 3 – Jul 2, 2026 with <model>". modelName: the
// /models entry's words for profile.model, or the alias itself once that model has left the list.
function learnedLine(profile, modelName) {
  if (!profile) return '';
  const r = profile.signedRange || { first: profile.from, last: profile.to };
  const parts = [`Version ${profile.version}`];
  const span = r && r.first && r.last ? ` signed ${dateSpan(r.first, r.last)}` : '';
  if (profile.notes != null) parts.push(`learned from ${profile.notes} notes${span}${modelName ? ` with ${modelName}` : ''}`);
  return parts.join(' · ');
}

// One history row's reason in words (screen 6 §4). 8b-2's automatic updates carry their own
// evidence sentence after "updated:".
function historyLine(row) {
  const reason = String((row && row.changeReason) || '');
  if (reason === 'manual') return 'saved by you'; // in Preferences or from the in-chart panel
  if (reason === 'learned') {
    const r = row.signedRange || { first: row.from, last: row.to };
    const span = r && r.first && r.last ? ` signed ${dateSpan(r.first, r.last)}` : '';
    return row.notes != null ? `approved by you: learned from ${row.notes} notes${span}` : 'approved by you';
  }
  if (reason === 'standard') return 'standard style: nothing learned in use';
  if (reason === 'restored_learned') return 'your learned style, back in use';
  if (reason === 'phrase_removed') return 'a phrase you removed';
  if (reason === 'habit_removed') return 'a habit you removed';
  const reverted = /^reverted:(\d+)$/.exec(reason);
  if (reverted) return `restored from version ${reverted[1]}`;
  if (reason.startsWith('updated:')) return `updated automatically: ${reason.slice('updated:'.length).trim()}`;
  return reason;
}

// When a version was approved: approved_at is a full timestamp (not a 'YYYY-MM-DD' string), so the
// browser's own day is the right one. → 'Oct 18, 2026', or '' when unreadable
function approvedDate(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// An automatic update (PR 8b-2) is never silent: until this physician opens Your style or sets the
// notice aside, the inbox says what changed and why. → { id, date, text } or null
function updateNoticeKey(id) { return `ne2-style-update:${id}`; }
function updateNotice(view, storage) {
  const a = view && view.approved;
  if (!a || !String(a.changeReason || '').startsWith('updated:')) return null;
  try { if (storage && storage.getItem(updateNoticeKey(a.id))) return null; } catch (_) { /* it shows */ }
  return { id: a.id, date: approvedDate(a.approvedAt), text: historyLine(a) };
}
function markUpdateSeen(storage, id) {
  try { if (storage && id) storage.setItem(updateNoticeKey(id), '1'); } catch (_) { /* private mode: it shows again */ }
}

// The inbox banner (mockup "Your style profile is ready to review"): shown while a learned draft
// waits, until this physician sets that draft aside. storage: window.localStorage, or null.
function bannerKey(draftId) { return `ne2-style-banner:${draftId}`; }
function bannerShows(view, storage) {
  const d = view && view.draft;
  if (!d || d.state !== 'learned') return false;
  try { return !(storage && storage.getItem(bannerKey(d.id))); } catch (_) { return true; }
}
function dismissBanner(storage, draftId) {
  try { if (storage) storage.setItem(bannerKey(draftId), '1'); } catch (_) { /* private mode: it shows again */ }
}

module.exports = {
  SECTION_LABELS, PRESETS, sectionLabel, sectionOrder, habitsOf, systemLabel, countWords, presetLabel, presetRange, rangeProblem,
  crossesExpansion, expansionWarning, shownProfile, styleChip, dateSpan, learnedLine, historyLine, approvedDate, bannerKey,
  bannerShows, dismissBanner, updateNoticeKey, updateNotice, markUpdateSeen,
};
